import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import axios from "axios";
import { Box, Typography, Button, TextField, MenuItem, Select, InputLabel, FormControl, List, ListItem, ListItemText, IconButton } from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';

const API_URL = process.env.REACT_APP_API_URL;
const API_KEY = process.env.REACT_APP_API_KEY;
const API_SECRET = process.env.REACT_APP_API_SECRET;

export default function NetworkDrivesPage({ appGroupId }) {
  // Forcer temporairement l'id du groupe à 1 si non fourni
  const effectiveGroupId = appGroupId || 1;
  const [drives, setDrives] = useState([]);
  const [newDrive, setNewDrive] = useState({ 
    letter: "V", 
    type: "CIFS", 
    server: "",
    share: "",
    path: "", 
    username: "", 
    password: "" 
  });
  const { t } = useTranslation();

  // Charger les drives au montage
  useEffect(() => {
    if (!effectiveGroupId) return;
    axios.get(`${API_URL}/app-groups/${effectiveGroupId}/network-drives`, {
      headers: {
        "x-api-key": API_KEY,
        "x-api-secret": API_SECRET,
      }
    }).then(res => setDrives(res.data)).catch(() => setDrives([]));
  }, [effectiveGroupId]);

  // Construire le chemin complet pour CIFS
  const buildPath = (drive) => {
    if (drive.type === 'CIFS') {
      return `\\\\${drive.server}\\${drive.share}${drive.path ? '\\' + drive.path : ''}`;
    }
    return drive.path;
  };

  // Ajouter un drive
  const handleAddDrive = () => {
    // Validation des champs requis
    if (newDrive.type === 'CIFS' && (!newDrive.server || !newDrive.share)) {
      alert('Veuillez remplir les champs serveur et partage pour CIFS');
      return;
    } else if (!newDrive.path && newDrive.type !== 'CIFS') {
      alert('Veuillez remplir le chemin du lecteur');
      return;
    }

    const driveToAdd = {
      ...newDrive,
      path: buildPath(newDrive)
    };

    axios.post(`${API_URL}/app-groups/${effectiveGroupId}/network-drives`, driveToAdd, {
      headers: {
        "x-api-key": API_KEY,
        "x-api-secret": API_SECRET,
      }
    }).then(res => setDrives(drives => [...drives, res.data]));
    
    // Réinitialiser le formulaire
    setNewDrive({ 
      letter: "V", 
      type: "CIFS", 
      server: "",
      share: "",
      path: "", 
      username: "", 
      password: "" 
    });
  };

  // Supprimer un drive
  const handleDeleteDrive = driveId => {
    axios.delete(`${API_URL}/network-drives/${driveId}`, {
      headers: {
        "x-api-key": API_KEY,
        "x-api-secret": API_SECRET,
      }
    }).then(() => setDrives(drives => drives.filter(d => d.id !== driveId)));
  };

  return (
    <Box sx={{ p: 4, maxWidth: 500, margin: 'auto' }}>
      <Typography variant="h5" mb={2}>{t('networkDrives.title')}</Typography>
      <Box mb={2}>
        <Box display="flex" gap={2} mb={2}>
          <FormControl sx={{ minWidth: 100 }}>
            <InputLabel>Lettre</InputLabel>
            <Select
              value={newDrive.letter}
              label="Lettre"
              onChange={e => setNewDrive(d => ({ ...d, letter: e.target.value }))}
              sx={{ height: '56px' }}
            >
              {Array.from({length: 26}, (_, i) => String.fromCharCode(65 + i)).map(char => (
                <MenuItem key={char} value={char}>{char}:</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Protocole</InputLabel>
            <Select
              value={newDrive.type}
              label="Protocole"
              onChange={e => setNewDrive(d => ({ ...d, type: e.target.value }))}
              sx={{ height: '56px' }}
            >
              <MenuItem value="CIFS">CIFS/SMB</MenuItem>
              <MenuItem value="NFS">NFS</MenuItem>
              <MenuItem value="FTP">FTP</MenuItem>
              <MenuItem value="SFTP">SFTP</MenuItem>
              <MenuItem value="WebDAV">WebDAV</MenuItem>
            </Select>
          </FormControl>
        </Box>
        {newDrive.type !== 'CIFS' && (
          <TextField
            label={t('networkDrives.path')}
            value={newDrive.path}
            onChange={e => setNewDrive(d => ({ ...d, path: e.target.value }))}
            fullWidth
            sx={{ 
              '& .MuiOutlinedInput-root': {
                height: '56px',
                '& input': {
                  padding: '16.5px 14px',
                },
              },
            }}
          />
        )}
        {newDrive.type === 'CIFS' && (
          <Box mb={2} display="flex" gap={2} flexWrap="wrap">
            <TextField
              label="Serveur"
              value={newDrive.server}
              onChange={e => setNewDrive(d => ({ ...d, server: e.target.value }))}
              sx={{ 
                flex: '1 1 300px',
                '& .MuiOutlinedInput-root': {
                  height: '56px',
                  '& input': {
                    padding: '16.5px 14px',
                  },
                },
              }}
              placeholder="adresse-ip-ou-nom"
            />
            <TextField
              label="Partage"
              value={newDrive.share}
              onChange={e => setNewDrive(d => ({ ...d, share: e.target.value }))}
              sx={{ 
                flex: '1 1 200px',
                '& .MuiOutlinedInput-root': {
                  height: '56px',
                  '& input': {
                    padding: '16.5px 14px',
                  },
                },
              }}
              placeholder="nom-du-partage"
            />
            <TextField
              label="Chemin (optionnel)"
              value={newDrive.path}
              onChange={e => setNewDrive(d => ({ ...d, path: e.target.value }))}
              sx={{ 
                flex: '1 1 200px',
                '& .MuiOutlinedInput-root': {
                  height: '56px',
                  '& input': {
                    padding: '16.5px 14px',
                  },
                },
              }}
              placeholder="dossier/sous-dossier"
            />
          </Box>
        )}
        {newDrive.type === 'CIFS' && (
          <Box mb={2} display="flex" gap={2}>
            <TextField
              label={t('networkDrives.username')}
              value={newDrive.username}
              onChange={e => setNewDrive(d => ({ ...d, username: e.target.value }))}
              sx={{ 
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  height: '56px',
                  '& input': {
                    padding: '16.5px 14px',
                  },
                },
              }}
            />
            <TextField
              label={t('networkDrives.password')}
              type="password"
              value={newDrive.password}
              onChange={e => setNewDrive(d => ({ ...d, password: e.target.value }))}
              sx={{ 
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  height: '56px',
                  '& input': {
                    padding: '16.5px 14px',
                  },
                },
              }}
            />
          </Box>
        )}
      </Box>
      <Button 
        variant="contained" 
        onClick={handleAddDrive} 
        disabled={newDrive.type === 'CIFS' ? !(newDrive.server && newDrive.share) : !newDrive.path}
      >
        {t('networkDrives.add')}
      </Button>
      <Box mt={4}>
        <Typography variant="subtitle1" gutterBottom>{t('networkDrives.drives')}</Typography>
        <List>
          {drives.map((drive) => (
            <ListItem
              key={drive.id}
              secondaryAction={
                <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteDrive(drive.id)}>
                  <DeleteIcon />
                </IconButton>
              }
            >
              <ListItemText
                primary={`[${drive.letter}:] ${drive.type} - ${drive.path}`}
                secondary={drive.type === 'CIFS' && drive.username ? `Utilisateur: ${drive.username}` : ''}
              />
            </ListItem>
          ))}
          {drives.length === 0 && <ListItem><ListItemText primary={t('networkDrives.noDrives')} /></ListItem>}
        </List>
      </Box>
    </Box>
  );
}
